import { Dropdown } from "flowbite-react";
import { Spinner } from "../components/atoms/Spinner";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  HiArrowLeft,
  HiDotsVertical,
  HiOutlineExclamationCircle,
  HiOutlineTrash,
  HiOutlineUserGroup,
} from "react-icons/hi";
import { HiOutlineEnvelope } from "react-icons/hi2";
import useAuth from "../hooks/useAuth";

import UsersListComponent from "../components/pages/dashboard/users/UsersListComponent";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  useDeleteGroup,
  useDeleteUsersFromGroup,
  useGetGroup,
  useGetGroupUsers,
  useUpdateGroup,
} from "../hooks/groups";
import SendPersonalMessage from "../components/pages/dashboard/entities/SendPersonalMessage";
import SendMessageChannelModal from "../components/pages/dashboard/entities/SendMessageChannelModal";
import { useTenantSendContext } from "../components/pages/dashboard/entities/sendPersonalMessage/useTenantSendContext";
import {
  SEND_MESSAGE_TYPE,
  USER_LIST_COMPONENT_TYPE,
} from "../constants/types";
import UpgradeModal from "../components/modals/UpgradeModal";
import { PremiumBenefit } from "../components/atoms/UpgradePromptContent";
import { PlanTier, usePermissions } from "../utils/permissions";
import { Cap, Level } from "../utils/rbacCatalog";
import { CustomInput } from "../components/atoms/CustomInput";
import { CustomButton } from "../components/atoms/CustomButton";
import { ModalShell } from "../components/atoms/ModalShell";
import SideDrawer from "../components/atoms/SideDrawer";
import SecureStorage from "../utils/secureStorage";
import { Copy } from "../components/atoms/Copy";
import { FaSms } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import SendSMSModal from "../components/pages/dashboard/entities/SendSMSModal";
import SendWhatsAppModal from "../components/pages/dashboard/entities/SendWhatsAppModal";
import { AddCreditModal } from "../components/pages/dashboard/entities/AddCreditModal";
import { CharacterCounter } from "../components/atoms/CharacterCounter";
import NoTranslate from "../components/atoms/NoTranslate";
import { groupSchema } from "./groupSchema";

interface ViewEntityGroupProps {
  groupIdentification?: string;
  onClose?: () => void;
}

const ViewEntityGroup: React.FC<ViewEntityGroupProps> = ({
  groupIdentification: groupIdentificationProp,
  onClose,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const canWriteGroup = permissions.can(Cap.GROUP_MANAGEMENT, Level.WRITE);
  const canDeleteGroup = permissions.can(Cap.GROUP_MANAGEMENT, Level.DELETE);
  const params = useParams();
  const group_identification =
    groupIdentificationProp ?? params.group_identification;
  const inDrawer = groupIdentificationProp !== undefined;
  const navigate = useNavigate();
  const canSendEmail = permissions.can(Cap.SEND_EMAIL, Level.WRITE);
  const canSendSms = permissions.can(Cap.SEND_SMS, Level.WRITE);
  const canSendWhatsapp = permissions.can(Cap.SEND_WHATSAPP, Level.WRITE);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [showSendMsgModal, setShowSendMsgModal] = useState(false);

  const sendMessageType =
    selectedRowIds.size > 0 ? SEND_MESSAGE_TYPE.MULTI : SEND_MESSAGE_TYPE.ALL;

  const sendContext = useTenantSendContext({
    customer_identification,
    user,
    entity_identification: user.entity_identification,
    entity_user_identification: customer_identification,
    toSendMsgUserId: "",
    group_identification: group_identification as string,
    users: selectedRowIds,
    type: sendMessageType,
    show: showSendMsgModal,
    close: () => setShowSendMsgModal(false),
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSMSUpgradeModal, setShowSMSUpgradeModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [showUpdateGroupModal, setShowUpdateGroupModal] = useState(false);
  const [excludeModal, setExcludeModal] = useState(false);
  const [offset, setOffset] = useState(0);
  const protectedGroups = ["BUSINESS CARDS"];

  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    if (inDrawer) setDrawerOpen(true);
  }, [inDrawer]);

  const hasSMSAccess = permissions.hasFeatureAccess("SEND_SMS");
  const hasWhatsappAccess = permissions.hasFeatureAccess("SEND_WHATSAPP");
  const hasMessageAccess = permissions.hasFeatureAccess("SEND_MESSAGES");

  const messagingBenefits: PremiumBenefit[] = [
    {
      id: "unlimited-messaging",
      text: t("dashboard.upgrade.messaging.benefit1") as string,
    },
    {
      id: "bulk-messaging",
      text: t("dashboard.upgrade.messaging.benefit2") as string,
    },
    {
      id: "custom-templates",
      text: t("dashboard.upgrade.messaging.benefit3") as string,
    },
    {
      id: "scheduled-messages",
      text: t("dashboard.upgrade.messaging.benefit4") as string,
    },
  ];

  const whatsappBenefits: PremiumBenefit[] = [
    {
      id: "whatsapp-reach",
      text: t("dashboard.upgrade.whatsapp.benefit1") as string,
    },
    {
      id: "whatsapp-templates",
      text: t("dashboard.upgrade.whatsapp.benefit2") as string,
    },
    {
      id: "whatsapp-pricing",
      text: t("dashboard.upgrade.whatsapp.benefit3") as string,
    },
  ];

  const smsBenefits: PremiumBenefit[] = [
    {
      id: "sms-bulk-messaging",
      text: t("dashboard.upgrade.sms.benefit1") as string,
    },
    {
      id: "sms-templates",
      text: t("dashboard.upgrade.sms.benefit2") as string,
    },
    {
      id: "sms-tracking",
      text: t("dashboard.upgrade.sms.benefit3") as string,
    },
    {
      id: "sms-analytics",
      text: t("dashboard.upgrade.sms.benefit4") as string,
    },
  ];
  const queryData = {
    groupId: group_identification as string,
    customer_identification: customer_identification,
    entityIdentification: user.entity_identification,
  };
  const {
    handleSubmit,
    watch,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: joiResolver(groupSchema),
  });
  const [limit, setLimit] = useState<12 | 30 | 50>(
    SecureStorage.getUsersListLimit() as 12 | 30 | 50
  );
  const [showSendSMSModal, setShowSendSMSModal] = useState(false);
  const [showSendWhatsAppModal, setShowSendWhatsAppModal] = useState(false);
  const [showWhatsappUpgradeModal, setShowWhatsappUpgradeModal] =
    useState(false);
  const [showWhatsAppAddCreditModal, setShowWhatsAppAddCreditModal] =
    useState(false);
  const [showAddCreditModal, setShowAddCreditModal] = useState(false);

  const { mutate: deleteGroup, isLoading: isDeletingGroup } = useDeleteGroup();
  const { mutate: updateGroup, isLoading: isUpdatingGroup } = useUpdateGroup();
  const { mutate: removeUsers, isLoading: isRemoving } =
    useDeleteUsersFromGroup();
  const {
    isLoading,
    isError,
    data: groupData,
    refetch,
  } = useGetGroup(queryData);

  const {
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
    isSuccess,
    data: dataUsers,
    refetch: refetchUsers,
  } = useGetGroupUsers({ ...queryData, limit: limit, pageParam: offset });

  const handleDeleteUserFromGroup = () => {
    removeUsers(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        group_identification: group_identification,
        users: Array.from(selectedRowIds.values()),
      },
      {
        onSuccess: () => {
          setExcludeModal(false);
          setSelectedRowIds(new Set());
          refetchUsers();
        },
      }
    );
  };

  useEffect(() => {
    setLimit(dataUsers?.pagination.limit ?? limit);
    setOffset(dataUsers?.pagination.offset ?? offset);
  }, [dataUsers, limit, offset]);

  const handleDeleteGroup = () => {
    deleteGroup(
      {
        ...queryData,
      },
      {
        onSuccess: () => {
          setOpenModal(false);
          if (inDrawer) {
            onClose?.();
          } else {
            navigate(-1);
          }
        },
      }
    );
  };

  const handleUpdateGroup = () => {
    reset(groupData);
    setShowUpdateGroupModal(true);
  };

  const onSubmitUpdate = (values: any) => {
    updateGroup(
      {
        ...queryData,
        data: values,
      },
      {
        onSuccess: () => {
          setShowUpdateGroupModal(false);
          refetch();
        },
      }
    );
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    onClose?.();
  };

  const loading = isLoading || isLoadingUsers;
  const errored = isError || isErrorUsers;

  const editButton =
    canWriteGroup && !protectedGroups.includes(groupData?.group_name) ? (
      <CustomButton
        onClick={() => handleUpdateGroup()}
        disabled={userEndedSubscription}
        data-svg-wrapper
        size="icon"
        icon="pencil"
        variant="none"
        tooltip={t("dashboard.groupList.key1") as string}
      />
    ) : null;

  const metaBlock = (
    <div className="self-stretch flex-col justify-start items-start gap-2 flex">
      <div className="self-stretch text-gray-500 text-sm font-normal font-figtree leading-tight">
        {t("dashboard.groupList.groupId")}:&nbsp;
        <Copy target={group_identification as string} truncate />
      </div>
      <div className="self-stretch text-gray-500 text-sm font-normal font-figtree leading-tight">
        {dataUsers?.pagination?.total} {t("dashboard.groupList.userCount")}
      </div>
    </div>
  );

  const sendActionLabel =
    selectedRowIds?.size > 0
      ? (t("dashboard.groupList.sendToSelectedUsers") as string)
      : (t("dashboard.groupList.sendToAll") as string);

  const groupActionButtons = (
    <>
      {(canSendEmail || canSendSms) && (
        <CustomButton
          className="min-w-[44px] sm:px-4 px-2"
          variant="primary"
          icon="envelop"
          onClick={() => setShowChannelModal(true)}
          disabled={userEndedSubscription}
          tooltip={sendActionLabel}
          title={<span className="hidden sm:inline">{sendActionLabel}</span>}
        />
      )}

      {selectedRowIds.size > 0 && (
        <CustomButton
          onClick={() => {
            setExcludeModal(true);
          }}
          className="min-w-[44px] px-2 sm:px-4"
          variant="secondary"
          title={
            <span className="hidden sm:inline">
              {t("dashboard.groupList.excludeFromGroup")}
            </span>
          }
          icon="userMinus"
        />
      )}

      {canDeleteGroup && !protectedGroups.includes(groupData?.group_name) && (
        <Dropdown
          label=""
          renderTrigger={() => (
            <button
              type="button"
              aria-label={t("dashboard.groupList.title-01") as string}
              className="bg-white rounded-md shadow-sm border border-gray-300 hover:bg-gray-50 flex justify-center items-center px-2 sm:px-4 min-h-[44px] sm:min-h-[38px] min-w-[44px] sm:min-w-[38px] ml-auto"
            >
              <HiDotsVertical size={22} className="text-gray-400 shrink-0" />
            </button>
          )}
          className="font-medium font-figtree leading-tight"
        >
          <Dropdown.Item
            onClick={() => setOpenModal(true)}
            disabled={userEndedSubscription}
          >
            {isDeletingGroup ? (
              <Spinner />
            ) : (
              <>
                <HiOutlineTrash className="mr-2 text-red-400" size={18} />
                {t("dashboard.groupList.deleteGroup")}
              </>
            )}
          </Dropdown.Item>
        </Dropdown>
      )}
    </>
  );

  const usersList = (
    <div className="px-2 pb-3">
      <UsersListComponent
        users={dataUsers?.data}
        isError={isErrorUsers}
        isLoading={isLoadingUsers}
        isSuccess={isSuccess}
        refetch={refetchUsers}
        selectedRowIds={selectedRowIds}
        setSelectedRowIds={setSelectedRowIds}
        type={USER_LIST_COMPONENT_TYPE.GROUPS}
        pagination={dataUsers?.pagination}
        offset={offset}
        setOffset={setOffset}
        limit={limit}
        setLimit={setLimit}
        singleColumn={inDrawer}
      />
    </div>
  );

  const overlays = (
    <>
      <ModalShell
        show={excludeModal}
        size="md"
        onClose={() => setExcludeModal(false)}
        bareCloseButton
        footer={
          <>
            <CustomButton
              onClick={() => {
                setExcludeModal(false);
              }}
              title={t("dashboard.entities.viewEntityUser.key11") as string}
            />
            <CustomButton
              variant="danger"
              onClick={handleDeleteUserFromGroup}
              isLoading={isRemoving}
              disabled={isRemoving}
              title={t("dashboard.entities.viewEntityUser.key10") as string}
            />
          </>
        }
      >
        <div className="text-center">
          <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t("dashboard.groupList.excludeUsersTitle")}
          </h3>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("dashboard.groupList.excludeUsersNote")}
          </p>
        </div>
      </ModalShell>

      <ModalShell
        show={openModal}
        size="md"
        onClose={() => setOpenModal(false)}
        bareCloseButton
        footer={
          <>
            <CustomButton
              onClick={() => {
                setOpenModal(false);
              }}
              title={t("dashboard.groupList.key5") as string}
            />
            <CustomButton
              variant="danger"
              onClick={handleDeleteGroup}
              title={t("dashboard.groupList.key4") as string}
            />
          </>
        }
      >
        <div className="text-center">
          <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t("dashboard.groupList.key3")}
          </h3>
        </div>
      </ModalShell>

      <ModalShell
        show={showUpdateGroupModal}
        onClose={() => setShowUpdateGroupModal(false)}
        title={t("dashboard.groupList.key1")}
        onSubmit={handleSubmit(onSubmitUpdate)}
        footer={
          <CustomButton
            type="submit"
            disabled={isUpdatingGroup}
            isLoading={isUpdatingGroup}
            variant="primary"
            title={t("common.update") as string}
          />
        }
      >
        <div>
          <div className="mb-6">
            <CustomInput
              name="group_name"
              control={control}
              error={errors.group_name}
              helperText={
                errors.group_name?.message
                  ? (t(errors.group_name.message as string) as string)
                  : undefined
              }
              label={t("dashboard.groupList.label-01") as string}
              maxLength={30}
            />
            <CharacterCounter
              warningThreshold={1}
              currentLength={watch("group_name")?.length || 0}
              maxLength={30}
            />
          </div>

          <div className="mb-6">
            <CustomInput
              rows={6}
              name="group_description"
              control={control}
              error={errors.group_description}
              helperText={
                errors.group_description?.message
                  ? (t(errors.group_description.message as string) as string)
                  : undefined
              }
              label={t("dashboard.groupList.label-02") as string}
              maxLength={250}
            />
            <CharacterCounter
              currentLength={watch("group_description")?.length || 0}
              maxLength={250}
            />
          </div>
        </div>
      </ModalShell>

      <SendMessageChannelModal
        show={showChannelModal}
        onClose={() => setShowChannelModal(false)}
        hasEmailAccess={hasMessageAccess}
        hasSmsAccess={hasSMSAccess}
        hasWhatsappAccess={hasWhatsappAccess}
        hasEmailCapability={canSendEmail}
        hasSmsCapability={canSendSms}
        hasWhatsappCapability={canSendWhatsapp}
        hasValidPhone
        onChoose={(channel) => {
          setShowChannelModal(false);
          if (channel === "email") {
            if (!hasMessageAccess) {
              setShowUpgradeModal(true);
            } else if (dataUsers?.data?.length > 0) {
              setShowSendMsgModal(true);
            }
          } else if (channel === "whatsapp") {
            hasWhatsappAccess
              ? setShowSendWhatsAppModal(true)
              : setShowWhatsappUpgradeModal(true);
          } else {
            hasSMSAccess
              ? setShowSendSMSModal(true)
              : setShowSMSUpgradeModal(true);
          }
        }}
      />

      <SendPersonalMessage
        show={showSendMsgModal}
        ctrFn={setShowSendMsgModal}
        onResolved={() => setSelectedRowIds(new Set())}
        type={sendMessageType}
        recipientGroupName={groupData?.group_name}
        sendContext={sendContext}
      />
      <UpgradeModal
        showModal={showUpgradeModal}
        setShowModal={setShowUpgradeModal}
        targetPlan={PlanTier.PREMIUM}
        title={t("dashboard.upgrade.messaging.title") as string}
        description={t("dashboard.upgrade.messaging.description") as string}
        benefits={messagingBenefits}
        icon={HiOutlineEnvelope}
        iconTheme="yellow"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
      <UpgradeModal
        showModal={showSMSUpgradeModal}
        setShowModal={setShowSMSUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.sms.title") as string}
        description={t("dashboard.upgrade.sms.description") as string}
        benefits={smsBenefits}
        icon={FaSms}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />

      <UpgradeModal
        showModal={showWhatsappUpgradeModal}
        setShowModal={setShowWhatsappUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.whatsapp.title") as string}
        description={t("dashboard.upgrade.whatsapp.description") as string}
        benefits={whatsappBenefits}
        icon={FaWhatsapp}
        iconTheme="green"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />

      <SendSMSModal
        show={showSendSMSModal}
        onClose={() => setShowSendSMSModal(false)}
        onAddCredit={() => {
          setShowSendSMSModal(false);
          setShowAddCreditModal(true);
        }}
        identification={group_identification as string}
        userName={groupData?.group_name}
        isBulkMessage={true}
        users={Array.from(selectedRowIds.values()) || []}
        recipientCount={
          selectedRowIds.size > 0
            ? selectedRowIds.size
            : dataUsers?.pagination?.total ?? 0
        }
        onResolved={() => setSelectedRowIds(new Set())}
      />

      <SendWhatsAppModal
        show={showSendWhatsAppModal}
        onClose={() => setShowSendWhatsAppModal(false)}
        onAddCredit={() => {
          setShowSendWhatsAppModal(false);
          setShowWhatsAppAddCreditModal(true);
        }}
        identification={group_identification as string}
        userName={groupData?.group_name}
        isBulkMessage={true}
        users={Array.from(selectedRowIds.values()) || []}
        recipientCount={
          selectedRowIds.size > 0
            ? selectedRowIds.size
            : dataUsers?.pagination?.total ?? 0
        }
        onResolved={() => setSelectedRowIds(new Set())}
      />

      <AddCreditModal
        show={showAddCreditModal}
        onClose={() => setShowAddCreditModal(false)}
      />

      <AddCreditModal
        kind="whatsapp"
        show={showWhatsAppAddCreditModal}
        onClose={() => setShowWhatsAppAddCreditModal(false)}
      />
    </>
  );

  if (inDrawer) {
    return (
      <SideDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        icon={HiOutlineUserGroup}
        title={
          groupData?.group_name || (t("dashboard.groupList.title-01") as string)
        }
        titleAction={!loading && !errored ? editButton : undefined}
        footer={
          <>
            {groupData && !loading && !errored && (
              <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 border-t border-gray-200 bg-white px-4 py-3">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {groupActionButtons}
                </div>
              </div>
            )}
            {overlays}
          </>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="sm" />
          </div>
        ) : errored ? (
          <div className="flex items-center justify-center py-16">
            <CustomButton
              onClick={() => {
                refetch();
                refetchUsers();
              }}
              title={t("dashboard.entities.viewEntityUser.btn-01") as string}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {groupData?.group_description && (
              <p className="whitespace-pre-line break-words text-sm text-gray-600">
                <NoTranslate>{groupData.group_description}</NoTranslate>
              </p>
            )}
            {metaBlock}
            {usersList}
          </div>
        )}
      </SideDrawer>
    );
  }

  if (loading)
    return (
      <span>
        <Spinner size="sm" />
      </span>
    );

  if (errored)
    return (
      <CustomButton
        onClick={() => {
          refetch();
          refetchUsers();
        }}
        title={t("dashboard.entities.viewEntityUser.btn-01") as string}
      />
    );

  return (
    <div className="entity-user-page">
      <div className="grid grid-cols-11">
        <div className="col-span-12">
          <div className="p-6 flex-col justify-start items-start gap-6 inline-flex bg-white w-full">
            <div className="self-stretch lg:justify-start lg:items-start justify-between items-center gap-2 inline-flex">
              <Link
                to={"/control-panel/groups"}
                className="gap-2 inline-flex items-center"
              >
                <div className="w-5 h-5 relative">
                  <HiArrowLeft size={20} className="text-gray-400" />
                </div>
                <div className="grow shrink basis-0 text-gray-500 text-sm font-semibold font-figtree leading-tight">
                  {t("dashboard.groupList.backToGroups")}
                </div>
              </Link>
            </div>

            <div className="self-stretch flex-col justify-start items-start gap-4 flex">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-gray-900">
                  <NoTranslate>{groupData?.group_name}</NoTranslate>
                </h1>
                {editButton}
              </div>
              {metaBlock}

              <div className="self-stretch flex gap-2 sm:gap-3">
                {groupActionButtons}
              </div>
            </div>
          </div>

          {usersList}
        </div>
      </div>

      {overlays}
    </div>
  );
};

export default ViewEntityGroup;
